import React, { useState } from 'react';
import { Mail, MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useLanguage } from '@/contexts/LanguageContext';

// Lazy load the actual Zoho form only when needed
const ZohoContactForm = React.lazy(() => import('./ZohoContactForm'));

const LazyZohoContactForm = () => {
  const { t } = useLanguage();
  const [showForm, setShowForm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleShowForm = async () => {
    setIsLoading(true);
    try {
      // Small delay to show loading state
      await new Promise(resolve => setTimeout(resolve, 300));
      setShowForm(true);
    } catch (error) {
      console.error('Failed to load contact form:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (!showForm) {
    return (
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
            <Mail className="w-8 h-8 text-primary" />
          </div>
          <CardTitle className="text-xl mb-2">Fale com a Quimical</CardTitle>
          <p className="text-sm text-muted-foreground">
            Entre em contato conosco para solicitar cotações, esclarecer dúvidas ou obter mais informações sobre nossos produtos.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <Button
            onClick={handleShowForm}
            disabled={isLoading}
            className="w-full bg-primary hover:bg-primary/90 text-primary-foreground"
            size="lg"
          >
            {isLoading ? (
              <>
                <div className="w-4 h-4 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Carregando...
              </>
            ) : (
              <>
                <Mail className="w-4 h-4 mr-2" />
                Abrir Formulário
              </>
            )}
          </Button>
          <Button
            variant="outline"
            onClick={() => window.open('https://wa.me/5537998606150', '_blank')}
            className="w-full"
            size="lg"
          >
            <MessageCircle className="w-4 h-4 mr-2" />
            WhatsApp Direto
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <React.Suspense
      fallback={
        <div className="w-full max-w-md p-8 bg-muted/30 rounded-lg flex items-center justify-center">
          <div className="text-center">
            <div className="w-6 h-6 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-sm text-muted-foreground">Carregando formulário...</p>
          </div>
        </div>
      }
    >
      <div className="space-y-4">
        <ZohoContactForm />
        <div className="text-center">
          <Button
            onClick={() => setShowForm(false)}
            variant="ghost"
            size="sm"
            className="text-xs"
          >
            Fechar Formulário
          </Button>
        </div>
      </div>
    </React.Suspense>
  );
};

export default LazyZohoContactForm;